import React, { useState, useEffect } from "react";
import {
  Avatar,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CssBaseline,
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import { Person, PlayArrow } from "@mui/icons-material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AxiosInstance from "../../services/networkServices";
import Spinner from "../../components/Spinner";
import VideoPlayerDialog from "../../components/VideoPlayerDialog";
import AccountActionDialog, {
  getAuthorAccountState,
} from "./AccountActionDialog";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  button: {
    backgroundColor: "red !important",
    color: "#fff !important",
    borderColor: "red !important",
    "&:hover": {
      backgroundColor: "red !important",
    },
    borderRadius: "6px !important",
    fontSize: "14px !important",
    height: "40px !important",
  },
}));

// Same cell styling as PostDetail/CommentDetail, extracted so the header row
// doesn't have to repeat it per column.
const headCellSx = {
  fontSize: { xs: "14px", sm: "15px", md: "15px", lg: "16px", xl: "16px" },
  color: "#000",
  fontWeight: "600",
  padding: {
    xs: "10px 5px",
    sm: "15px 10px",
    md: "15px 10px",
    lg: "20px 15px",
    xl: "20px 15px",
  },
  verticalAlign: { xs: "top", sm: "top", md: "top" },
  backgroundColor: "#e0e0e0",
};

const bodyCellSx = {
  fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "15px", xl: "15px" },
  color: "#333",
  padding: {
    xs: "10px 5px",
    sm: "10px",
    md: "10px",
    lg: "10px 15px",
    xl: "10px 15px",
  },
};

const HEADERS = [
  "Reason",
  "Reporter Username",
  "Reporter Email",
  "Description",
  "Date of Report",
];

const UserDetail = () => {
  const classes = useStyles();
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Live-stream reports share this page but must not mix into user reports.
  const reportType = location.state?.filterType === "LIVE" ? "LIVE" : "USER";
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [reporters, setReporters] = useState([]);
  const [openDisableDialog, setOpenDisableDialog] = useState(false);
  const [accountDialog, setAccountDialog] = useState({
    open: false,
    action: null,
  });
  const [accountActionBusy, setAccountActionBusy] = useState(false);
  const [video, setVideo] = useState({ open: false, url: "", both: false });

  useEffect(() => {
    const fetchReportedUsers = async () => {
      setLoading(true);
      try {
        const response = await AxiosInstance.post(
          "/report/list-reported-users",
          {
            page: 1,
            limit: 10,
            id: userId,
            ...(reportType === "LIVE" ? { type: "LIVE" } : {}),
          }
        );
        const { list } = response.data.data;
        // Reporter accounts can be deleted after reporting — the report row
        // must still render, so never assume userDetails exists.
        const formattedReporters = list.map((report) => ({
          id: report?._id,
          reason: report?.issue,
          username: report?.userDetails?.username || "Deleted user",
          email: report?.userDetails?.email || "—",
          description: report?.issueDescription,
          date: report?.createdAt,
          recordingUrl: report?.recordingUrl || "",
          recordingScope: report?.recordingScope || null,
        }));
        setReporters(formattedReporters);
        // Only the first row carries the reported user's account details.
        setUserInfo(list?.[0]?.reportedUserDetails || null);
      } catch (error) {
        console.error("Failed to fetch reported users", error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchReportedUsers();
    }
  }, [userId, reportType]);

  // A missing account document (or isDeleted) means there is nothing left to
  // disable — the reports themselves still render below.
  const accountState = getAuthorAccountState(userInfo);
  const canDisable = accountState === "active";
  const canBan = accountState === "active" || accountState === "disabled";

  const handleDisableUser = async () => {
    try {
      await AxiosInstance.post("/report/action", {
        action: "DISABLE",
        type: reportType,
        id: userId,
      });
      setUserInfo((prev) =>
        prev ? { ...prev, isAccountBlocked: true } : prev
      );
      setOpenDisableDialog(false);
    } catch (error) {
      console.error("Failed to disable user", error);
    }
  };

  const handleBanAccount = async () => {
    setAccountActionBusy(true);
    try {
      await AxiosInstance.post("/report/action", {
        action: "BAN_ACCOUNT",
        type: "LIVE",
        id: userId,
      });
      setUserInfo((prev) =>
        prev ? { ...prev, isAccountBlocked: true, isDeleted: true } : prev
      );
      setAccountDialog({ open: false, action: null });
    } catch (error) {
      console.error("Failed to ban user", error);
    } finally {
      setAccountActionBusy(false);
    }
  };

  const getAccountStatusChip = () => {
    if (accountState === "banned")
      return (
        <Chip
          label="Account Banned"
          size="small"
          color="error"
          sx={{ fontWeight: 600 }}
        />
      );
    if (accountState === "deleted")
      return (
        <Chip
          label="Account Deleted"
          size="small"
          sx={{ backgroundColor: "#9e9e9e", color: "#fff", fontWeight: 600 }}
        />
      );
    if (accountState === "disabled")
      return (
        <Chip
          label="Account Blocked"
          size="small"
          color="error"
          sx={{ fontWeight: 600 }}
        />
      );
    return (
      <Chip
        label="Account Active"
        size="small"
        color="success"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  const handleBreadcrumbClick = (event) => {
    event.preventDefault();
    navigate("/manage-ticket", {
      state: reportType === "LIVE" ? { filterType: "LIVE" } : undefined,
    });
  };

  const formatDateString = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const fullName = [userInfo?.firstName, userInfo?.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <CssBaseline />
      <Box>
        {loading ? (
          <Spinner />
        ) : (
          <Box>
            <Box sx={{ marginBottom: "24px" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: {
                        xs: "15px",
                        sm: "16px",
                        md: "18px",
                        lg: "20px",
                        xl: "24px",
                      },
                    }}
                    component="h1"
                  >
                    {reportType === "LIVE"
                      ? "Live Stream Reports"
                      : "User Reports Details"}
                  </Typography>
                  {getAccountStatusChip()}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Button
                    onClick={() => setOpenDisableDialog(true)}
                    variant="contained"
                    color="error"
                    disabled={!canDisable}
                    sx={{
                      borderRadius: "6px",
                      fontSize: "14px",
                      backgroundColor: "red",
                    }}
                  >
                    {accountState === "disabled"
                      ? "Account Blocked"
                      : accountState === "banned"
                      ? "Account Banned"
                      : accountState === "deleted"
                      ? "Account Deleted"
                      : "Disable User"}
                  </Button>
                  {reportType === "LIVE" && (
                    <Button
                      onClick={() =>
                        setAccountDialog({ open: true, action: "BAN_ACCOUNT" })
                      }
                      variant="contained"
                      color="error"
                      disabled={!canBan || accountActionBusy}
                      sx={{
                        borderRadius: "6px",
                        fontSize: "14px",
                        backgroundColor: "red",
                      }}
                    >
                      {accountState === "banned"
                        ? "Account Banned"
                        : accountState === "deleted"
                        ? "Account Deleted"
                        : "Ban account"}
                    </Button>
                  )}
                </Box>
              </Box>
              <Breadcrumbs aria-label="breadcrumb">
                <Link
                  underline="hover"
                  color="inherit"
                  href="#"
                  onClick={handleBreadcrumbClick}
                >
                  Reports
                </Link>
                <Typography color="textPrimary">
                  {reportType === "LIVE" ? "Live Stream" : "User Details"}
                </Typography>
              </Breadcrumbs>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                padding: "12px 15px",
                marginBottom: "15px",
                borderRadius: "6px",
                backgroundColor: "#f7f7f7",
                border: "1px solid #e0e0e0",
              }}
            >
              <Avatar
                src={userInfo?.profilePicture || undefined}
                alt={userInfo?.username || "Reported user"}
                sx={{
                  width: 64,
                  height: 64,
                  border: "2px solid #142C5C",
                  bgcolor: "#e0e0e0",
                }}
              >
                {!userInfo?.profilePicture && (
                  <Person sx={{ fontSize: 32, color: "#777" }} />
                )}
              </Avatar>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#142C5C",
                    fontSize: "16px",
                  }}
                >
                  {userInfo?.username
                    ? `@${userInfo.username}`
                    : "Deleted user"}
                </Typography>
                {fullName && (
                  <Typography sx={{ fontSize: "14px", color: "#333" }}>
                    {fullName}
                  </Typography>
                )}
                {userInfo?.email && (
                  <Typography sx={{ fontSize: "13px", color: "#777" }}>
                    {userInfo.email}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                padding: "15px",
                borderRadius: "6px",
                boxShadow:
                  "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
              }}
            >
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: "calc(100vh - 200px)",
                  boxShadow: "none !important",
                }}
              >
                <Table
                  sx={{
                    border: "1px solid rgba(224, 224, 224, 1)",
                    minWidth: "100%",
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {(reportType === "LIVE"
                        ? [...HEADERS, "Recording"]
                        : HEADERS
                      ).map((header) => (
                        <TableCell key={header} sx={headCellSx}>
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reporters.map((reporter) => (
                      <TableRow key={reporter?.id}>
                        <TableCell sx={bodyCellSx}>
                          {reporter.reason}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {reporter.username}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>{reporter.email}</TableCell>
                        <TableCell sx={bodyCellSx}>
                          {reporter.description}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {formatDateString(reporter.date)}
                        </TableCell>
                        {reportType === "LIVE" && (
                          <TableCell sx={bodyCellSx}>
                            {reporter.recordingUrl ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<PlayArrow />}
                                  onClick={() =>
                                    setVideo({
                                      open: true,
                                      url: reporter.recordingUrl,
                                      both: reporter.recordingScope === "both",
                                    })
                                  }
                                  sx={{ borderRadius: "6px", textTransform: "none" }}
                                >
                                  Play
                                </Button>
                                {reporter.recordingScope === "both" && (
                                  <Chip size="small" label="Both people" />
                                )}
                              </Box>
                            ) : (
                              "Recording not ready"
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}
        {/* Dialog for disable confirmation */}
        <Dialog
          sx={{
            "& .MuiDialog-container .MuiPaper-elevation": {
              borderRadius: "8px",
              width: "380px",
            },
          }}
          open={openDisableDialog}
          onClose={() => setOpenDisableDialog(false)}
        >
          <DialogTitle
            sx={{
              padding: "30px 16px 0",
              textAlign: "center",
              fontWeight: 700,
              color: "red",
            }}
          >
            Confirmation
          </DialogTitle>
          <DialogContent sx={{ padding: "0 16px 20px" }}>
            <Typography sx={{ textAlign: "center", color: "#666" }}>
              Are you sure you want to disable this user's account? They will
              no longer be able to log in.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ padding: "0 16px 20px" }}>
            <Button
              onClick={() => setOpenDisableDialog(false)}
              sx={{
                border: "1px solid #888",
                color: "#888",
                borderRadius: "6px",
                fontSize: "14px",
                "&:hover": {
                  backgroundColor: "#b9b9b9",
                  color: "#222",
                  borderColor: "#b9b9b9",
                },
              }}
            >
              No
            </Button>
            <Button onClick={handleDisableUser} className={classes.button}>
              Yes
            </Button>
          </DialogActions>
        </Dialog>
        <AccountActionDialog
          open={accountDialog.open}
          action={accountDialog.action}
          username={userInfo?.username}
          subject="live"
          busy={accountActionBusy}
          onClose={() => setAccountDialog({ open: false, action: null })}
          onConfirm={handleBanAccount}
        />
        <VideoPlayerDialog
          open={video.open}
          onClose={() => setVideo({ open: false, url: "", both: false })}
          url={video.url}
          title={
            video.both ? "Reported live (both people)" : "Reported live"
          }
        />
      </Box>
    </>
  );
};

export default UserDetail;
